import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import HreflangMeta from '../components/HreflangMeta';
import { handleTalkToSpecialist } from '../utils/webchat';
import './PricingPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.libbenagents.com';

// ── Feature labels for comparison table ──────────────────────────────────────
const FEATURE_KEYS = [
  'knowledgeBase',
  'vision',
  'audio',
  'documents',
  'webchat',
  'voice',
  'feegow',
  'scheduledTasks',
  'overageEnabled',
];

function getCurrency(language) {
  if (language === 'en') return 'USD';
  return 'BRL';
}

function formatPrice(value, currency) {
  if (value === null || value === undefined || value === '—') return '—';
  if (value === 0) return currency === 'BRL' ? 'R$ 0' : '$0';
  if (currency === 'BRL') return `R$ ${Number(value).toLocaleString('pt-BR')}`;
  return `$${Number(value).toLocaleString('en-US')}`;
}

function formatLimit(value, t) {
  if (value === -1) return t('pricingPage.unlimited');
  if (value === null || value === undefined) return '—';
  return String(value).toLocaleString();
}

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const [tiers, setTiers] = useState(null);
  const [error, setError] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const currency = getCurrency(i18n.language);

  useEffect(() => {
    fetch(`${API_URL}/public/tiers`)
      .then((r) => {
        if (!r.ok) throw new Error('API error');
        return r.json();
      })
      .then((data) => {
        const list = data?.tiers;
        if (list?.length) {
          setTiers(list.sort((a, b) => (a.order || 0) - (b.order || 0)));
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true));
  }, []);

  const togglePeriod = useCallback(() => {
    setBillingPeriod((prev) => (prev === 'monthly' ? 'annual' : 'monthly'));
  }, []);

  const getPrice = (tier) => {
    const priceObj = tier.prices?.[currency];
    if (!priceObj) return '—';
    return billingPeriod === 'annual'
      ? priceObj.annual !== null ? Math.round(priceObj.annual / 12) : null
      : priceObj.monthly;
  };

  return (
    <>
      <Helmet>
        <title>{t('pricingPage.metaTitle')}</title>
        <meta name="description" content={t('pricingPage.metaDescription')} />
        <meta property="og:title" content={t('pricingPage.metaTitle')} />
        <meta property="og:description" content={t('pricingPage.metaDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://libbenagents.com/precos" />
        <html lang={i18n.language} />
      </Helmet>
      <HreflangMeta />

      <main className="pricing-page">
        <section className="pricing-page-hero">
          <h1>{t('pricingPage.title')}</h1>
          <p className="pricing-page-subtitle">{t('pricingPage.subtitle')}</p>

          {/* Billing toggle */}
          <div className="billing-toggle">
            <span className={billingPeriod === 'monthly' ? 'active' : ''}>
              {t('pricingPage.monthly')}
            </span>
            <button
              className="toggle-switch"
              onClick={togglePeriod}
              aria-label={t('pricingPage.toggleLabel')}
              role="switch"
              aria-checked={billingPeriod === 'annual'}
            >
              <span className={`toggle-knob ${billingPeriod === 'annual' ? 'annual' : ''}`} />
            </button>
            <span className={billingPeriod === 'annual' ? 'active' : ''}>
              {t('pricingPage.annual')}
              <span className="annual-badge">{t('pricingPage.annualDiscount')}</span>
            </span>
          </div>
        </section>

        {/* Error / fallback state */}
        {error && (
          <section className="pricing-page-fallback">
            <p>{t('pricingPage.fallbackMessage')}</p>
            <button type="button" onClick={handleTalkToSpecialist} className="btn-pricing-cta">
              {t('pricingPage.fallbackCta')}
            </button>
          </section>
        )}

        {/* Loading skeleton */}
        {!error && tiers === null && (
          <section className="pricing-page-loading">
            <div className="pricing-skeleton-grid">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="pricing-skeleton-card" />
              ))}
            </div>
          </section>
        )}

        {/* Plan cards */}
        {!error && tiers && (
          <section className="pricing-page-cards">
            <div className="pricing-cards-grid">
              {tiers.map((tier, idx) => {
                const price = getPrice(tier);
                const isHighlighted = idx === Math.min(2, tiers.length - 1);
                return (
                  <div
                    key={tier.tierId}
                    className={`pricing-plan-card ${isHighlighted ? 'highlighted' : ''}`}
                  >
                    {isHighlighted && (
                      <div className="plan-popular-badge">{t('pricing.mostPopular')}</div>
                    )}
                    <h3 className="plan-card-name">{tier.name}</h3>
                    {tier.description && (
                      <p className="plan-card-description">{tier.description}</p>
                    )}
                    <div className="plan-card-price">
                      <span className="plan-price-value">
                        {price !== null ? formatPrice(price, currency) : t('pricingPage.contactUs')}
                      </span>
                      {price !== null && price !== '—' && (
                        <span className="plan-price-period">/{t('pricingPage.month')}</span>
                      )}
                    </div>
                    {billingPeriod === 'annual' && price !== null && (
                      <p className="plan-annual-note">
                        {t('pricingPage.billedAnnually')}
                      </p>
                    )}
                    <ul className="plan-card-limits">
                      <li>
                        <span className="limit-label">{t('pricingPage.credits')}</span>
                        <span className="limit-value">
                          {tier.monthlyCredits === -1
                            ? t('pricingPage.unlimited')
                            : tier.monthlyCredits > 0
                              ? Number(tier.monthlyCredits).toLocaleString()
                              : t('pricingPage.consult')}
                        </span>
                      </li>
                      <li>
                        <span className="limit-label">{t('pricing.agents')}</span>
                        <span className="limit-value">
                          {formatLimit(tier.limits?.agents, t)}
                        </span>
                      </li>
                      <li>
                        <span className="limit-label">{t('pricing.channels')}</span>
                        <span className="limit-value">
                          {formatLimit(tier.limits?.channels, t)}
                        </span>
                      </li>
                      <li>
                        <span className="limit-label">{t('pricingPage.members')}</span>
                        <span className="limit-value">
                          {formatLimit(tier.limits?.members, t)}
                        </span>
                      </li>
                    </ul>
                    <button
                      type="button"
                      onClick={handleTalkToSpecialist}
                      className={`plan-card-cta ${isHighlighted ? 'cta-primary' : 'cta-ghost'}`}
                    >
                      {price === 0 ? t('pricingPage.startFree') : t('pricing.cta')}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Feature comparison table */}
        {!error && tiers && tiers.length > 0 && (
          <section className="pricing-page-comparison">
            <h2>{t('pricingPage.comparisonTitle')}</h2>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>{t('pricingPage.feature')}</th>
                    {tiers.map((tier) => (
                      <th key={tier.tierId}>{tier.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_KEYS.map((key) => (
                    <tr key={key}>
                      <td className="feature-name">{t(`pricingPage.features.${key}`)}</td>
                      {tiers.map((tier) => (
                        <td key={tier.tierId} className="feature-cell">
                          {tier.features?.[key] ? (
                            <span className="feature-check">✓</span>
                          ) : (
                            <span className="feature-cross">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="pricing-page-cta">
          <h2>{t('pricingPage.ctaTitle')}</h2>
          <p>{t('pricingPage.ctaSubtitle')}</p>
          <button type="button" onClick={handleTalkToSpecialist} className="btn-pricing-cta">
            {t('pricingPage.ctaButton')}
          </button>
          <p className="pricing-cta-note">{t('pricingPage.ctaNote')}</p>
        </section>
      </main>
    </>
  );
}
